import { useEffect, useRef, useState } from 'react';
import {
  applyTabMessage,
  parseTabMessage,
  prunePeers,
  shouldReply,
  TAB_TTL_MS,
  type TabMessage,
} from './open-tabs';

// =====================================================================
// How many cockpit tabs this SDR has open, for the "close your other tabs" banner.
//
// Twilio's bridge TwiML rings `<Client><Identity>sdr_N</Identity>`, which reaches EVERY
// registered endpoint — so any tab can win the accept race — while the BFF pushes the card
// only to the session whose pool matches. Two tabs therefore split a call: one holds the
// audio, the other shows the card, and the SDR sits in silence while the lead hears an open
// office. Until a tab can be stopped from answering, the fastest mitigation is telling the
// SDR, in the tab they are looking at, that a second one exists.
//
// PING/PONG, NOT A PLAIN HEARTBEAT. The tab this warns about has usually been hidden for
// minutes, which puts its timers under Chrome's intensive throttling (~one beat a minute),
// so a tab that announced on its own schedule would look absent exactly when the warning is
// true. Message tasks are not timer throttled, so a hidden tab still answers a ping.
//
// It can still fall out of the count if Chrome FREEZES it (Energy Saver, hidden and silent
// past five minutes), because a frozen page runs no tasks at all. That is the one gap, and
// it is self-limiting: a tab too frozen to answer a ping is also too frozen to answer a
// call, which is the harm being warned about.
// =====================================================================

const TABS_CHANNEL = 'maestro-cockpit-tabs';
// The manual dialer's own presence channel: its tabs count each other on it, and the
// cockpit listens (without announcing) to say when one is open.
export const MANUAL_TABS_CHANNEL = 'maestro-manual-tabs';
const TAB_PING_MS = 3_000;
// A listener that does not ping hears its peers only on their own beats, and a hidden
// peer's beat degrades to about one a minute under Chrome's intensive throttling: its
// expiry must outlast that, or the banner would flap once a minute. A peer that
// leaves says `bye`, so the long expiry only covers one that died silently.
const PASSIVE_TTL_MS = 75_000;

// Unique per tab. `crypto.randomUUID` is secure-context only — CloudFront and localhost both
// qualify, a bare-IP dev host does not, and this is a presence key rather than anything
// security-bearing, so any unique string will do.
function newTabId(): string {
  return (
    crypto.randomUUID?.() ?? `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
  );
}

/**
 * Number of cockpit tabs open for `identity`, including this one. Always ≥ 1.
 *
 * Degrades to 1 where BroadcastChannel is unavailable (older browsers, some privacy modes):
 * the banner simply never shows, which is the behaviour without this hook at all.
 */
export function useOpenTabCount(
  identity: string,
  opts: { channel?: string; announce?: boolean } = {},
): number {
  const channel = opts.channel ?? TABS_CHANNEL;
  // A listener that does not announce is not a peer: it neither pings nor answers, so
  // the tabs it watches never count it, and it sees them on their own beats.
  const announce = opts.announce ?? true;
  const tabId = useRef<string>('');
  if (tabId.current === '') tabId.current = newTabId();

  const [count, setCount] = useState(1);

  useEffect(() => {
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(channel);
    } catch {
      return; // no channel: we cannot see siblings, so we claim none
    }

    const self = { tabId: tabId.current, identity };
    let peers = new Map<string, number>();
    setCount(1); // a fresh channel starts knowing nobody

    const commit = (next: Map<string, number>) => {
      if (next === peers) return;
      peers = next;
      setCount(peers.size + 1);
    };
    const post = (msg: TabMessage) => ch.postMessage(msg);
    const ping = (wantReply: boolean) =>
      post({ kind: 'here', tabId: self.tabId, identity, at: Date.now(), wantReply });

    ch.onmessage = (event: MessageEvent<unknown>) => {
      const msg = parseTabMessage(event.data);
      if (!msg) return;
      commit(applyTabMessage(peers, msg, self));
      // Answered from the message handler, never a timer: this is the path a throttled
      // background tab is still seen on.
      if (announce && shouldReply(msg, self)) ping(false);
    };

    if (announce) ping(true); // don't leave a fresh tab blind (or invisible) for a whole interval
    let lastPingAt = Date.now();
    let pingBeforeThat = lastPingAt;
    const beat = setInterval(() => {
      const now = Date.now();
      // Expire a peer only if it stayed silent through pings we actually SENT. Our own
      // interval degrades to ~1/min while this tab is hidden, so a wall-clock TTL would
      // expire every peer on every tick and flap the banner off and back once a minute.
      commit(
        prunePeers(
          peers,
          now,
          Math.max(announce ? TAB_TTL_MS : PASSIVE_TTL_MS, now - pingBeforeThat),
        ),
      );
      pingBeforeThat = lastPingAt;
      lastPingAt = now;
      if (announce) ping(true);
    }, TAB_PING_MS);

    // Returning to a tab is when the SDR reads the banner, so refresh it then rather than
    // showing whatever the throttled interval last managed.
    const onVisible = () => {
      if (announce && document.visibilityState === 'visible') ping(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    // pagehide, not unload: it is bfcache-compatible and fires on mobile teardown where
    // unload never does. Still best-effort — the TTL is what covers a tab that dies without
    // saying anything.
    const onHide = () => {
      if (announce) post({ kind: 'bye', tabId: self.tabId, identity });
    };
    window.addEventListener('pagehide', onHide);

    return () => {
      clearInterval(beat);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onHide);
      onHide(); // leaving the route retires this tab's presence too
      ch.close();
    };
  }, [identity, channel, announce]);

  return count;
}
